import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Check, Zap, Shield,
  Layers, Layout,
  TrendingUp, Clock, Eye, MessageSquare, Globe,
  ChevronRight,
  Inbox, Calendar as CalendarIcon, BellRing, Database, BarChart3,
} from "lucide-react";
import {
  fadeUp, stagger, TIERS, CARE_PLANS, MARQUEE_ITEMS, AI_HEADLINES,
  ServiceModal, ProcessStep,
} from "@/data/homeData";
import Tilt from "@/components/Tilt";
import FrameworkCarousel from "@/components/FrameworkCarousel";

const haptic = (ms = 10) => { try { navigator?.vibrate?.(ms); } catch {} };

export default function HomePage() {
  const [activeService, setActiveService] = useState(null);
  const [activeBanner, setActiveBanner] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);

  // Cinematic hero parallax
  const { scrollY } = useScroll();
  const heroParallaxY = useTransform(scrollY, [0, 800], [0, 180]);
  const heroParallaxScale = useTransform(scrollY, [0, 800], [1, 1.08]);
  const heroParallaxOpacity = useTransform(scrollY, [0, 600], [1, 0.55]);

  const BANNERS = [
    { headline: "Your marketing. Your ads. Your website. Your automation. All handled. All in one place.", support: "We run the marketing department for service businesses that want it done properly. One subscription. Real people behind it.", bg: "/hero_intelligence_1.webp" },
    { headline: "Built for service businesses that want growth without the agency runaround.", support: "From £497 a month, we handle your site, your AI front desk and your content. Pro Department at £1,997 a month runs the whole marketing function. You focus on the work.", bg: "/hero_intelligence_2.webp" },
    { headline: "We build the website. We run it. We update it.", support: "No retainer wars. No quote chasing. One subscription covers your full marketing engine. Cancel any time.", bg: "/hero_intelligence_3.webp" },
    { headline: "Marketing department. Without hiring one.", support: "Production. AI Workforce. Content Autopilot. Three arms working as one for under what a junior marketer costs.", bg: "/hero_intelligence_4.webp" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideDirection(1);
      setActiveBanner((prev) => (prev + 1) % BANNERS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [BANNERS.length]);

  const goToBanner = (i) => {
    setSlideDirection(i > activeBanner ? 1 : -1);
    setActiveBanner(i);
  };

  return (
    <main data-testid="home-page" className="overflow-x-hidden">
      <Helmet>
        <title>STRATIQ Solutions | SmartSite™ Websites for Service Businesses</title>
        <meta name="description" content="STRATIQ Solutions. Website designer, automation specialist, AI integration, app developer and brand consultant for 60+ service industries in London, Barking and across the UK. From electricians and plumbers to beauty salons and estate agents. Engineered for lead capture, conversion, brand optimisation and intelligent automation." />
        <meta name="keywords" content="web design London, website designer London, website designer UK, automation specialist, AI integration business, app builder London, app developer UK, brand consultant London, brand specialist, brand awareness, brand identity, brand optimisation, brand presentation, brand strategy, brand association, business consultant, business systems specialist, logo creation, digital brand consultancy, marketing systems, marketing infrastructure, conversion specialist, website optimisation, East London web agency, UK business websites, lead capture website, SmartSite™, STRATIQ Solutions, service business website, website for electricians, website for plumbers, website for builders, website for cleaners, website for beauty salons, website for barbers, website for personal trainers, website for mechanics, website for estate agents, website for accountants, website for solicitors, website for photographers, website for landscapers, website for HVAC, website for roofers, website for driving instructors, website for tutors, website for therapists, website for caterers, website for event planners, website for removal companies, small business web design London, tradesman website design, booking system website UK, business automation website" />
        <link rel="canonical" href="https://www.stratiqsolutions.co.uk/" />
        <meta property="og:title" content="STRATIQ Solutions | SmartSite™ Websites for UK Service Businesses" />
        <meta property="og:description" content="Stop losing leads to outdated websites. SmartSites™ capture every enquiry, automate follow ups and convert visitors into paying customers. Built for 60+ industries. Live in days." />
        <meta property="og:url" content="https://www.stratiqsolutions.co.uk/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="STRATIQ Solutions | SmartSite™ Websites for UK Service Businesses" />
        <meta name="twitter:description" content="Stop losing leads to outdated websites. SmartSites™ capture every enquiry, automate follow ups and convert visitors into paying customers." />
      </Helmet>

      <AnimatePresence>
        {activeService && <ServiceModal service={activeService} onClose={() => setActiveService(null)} />}
      </AnimatePresence>

      {/* ──── HERO ──── */}
      <section className="relative w-full min-h-[100svh] overflow-hidden flex items-center bg-slate-950" data-testid="hero-section">
        <motion.div className="absolute inset-0 will-change-transform" style={{ y: heroParallaxY, scale: heroParallaxScale, opacity: heroParallaxOpacity }}>
          <AnimatePresence initial={false}>
            <motion.div key={activeBanner} className="absolute inset-[-6%] will-change-transform"
              initial={{ scale: 1.18, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ scale: { type: "spring", stiffness: 40, damping: 15, mass: 1.4, restDelta: 0.001 }, opacity: { duration: 1.8, ease: [0.4, 0, 0.2, 1] } }}>
              <motion.img src={BANNERS[activeBanner].bg} alt="" className="w-full h-full object-cover" loading="eager" fetchPriority="high"
                animate={{ scale: [1, 1.06] }} transition={{ duration: 7, ease: "linear" }} />
            </motion.div>
          </AnimatePresence>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950/85 z-[1]" />
        <div className="absolute inset-0 opacity-[0.03] z-[1]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")", backgroundRepeat: "repeat", backgroundSize: "256px 256px" }} />

        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6 md:px-12 pt-32 pb-16 sm:py-36 md:py-44 w-full">
          <AnimatePresence mode="wait">
            <motion.div key={activeBanner}
              initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -16, scale: 1.01 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} className="text-center max-w-4xl mx-auto">
              <motion.div initial={{ width: 0 }} animate={{ width: 48 }} transition={{ delay: 0.3, duration: 0.6 }} className="h-[2px] bg-gradient-to-r from-amber-400 to-amber-500 mx-auto mb-8" />
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-[-0.03em] leading-[1.05] text-white mb-6 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]" data-testid="hero-headline" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontWeight: 700 }}>{BANNERS[activeBanner].headline}</h1>
              <p className="text-base md:text-lg text-white leading-relaxed max-w-2xl mx-auto mb-10 font-medium tracking-wide drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]">{BANNERS[activeBanner].support}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }} data-testid="hero-primary-cta" className="group inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-4 text-base font-semibold transition-all shadow-lg shadow-black/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 tactile-press cine-btn">Talk to a STRATIQ Advisor <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></button>
                <Link to="/pricing" data-testid="hero-secondary-cta" className="inline-flex items-center justify-center gap-2 text-white/80 border border-white/15 hover:border-white/30 hover:bg-white/5 rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 backdrop-blur-sm tactile-press">View Subscription Plans</Link>
              </div>
              {/* Platform icons + trust signal */}
              <div className="mt-12 flex flex-col items-center gap-5">
                <p className="text-[10px] tracking-[0.3em] uppercase text-slate-300/80 font-semibold">Marketing across every platform you need</p>
                <div className="flex items-center justify-center flex-wrap gap-x-7 gap-y-3 text-white/85" data-testid="hero-platform-icons">
                  {[
                    { name: "Facebook", path: "M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z" },
                    { name: "Instagram", path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" },
                    { name: "TikTok", path: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" },
                    { name: "YouTube", path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" },
                    { name: "LinkedIn", path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                    { name: "X", path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
                    { name: "Pinterest", path: "M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.747-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z" },
                    { name: "Threads", path: "M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.97-3.91-5.997-8.34-6.028-2.925.022-5.135.946-6.57 2.747-1.343 1.685-2.037 4.122-2.063 7.245.026 3.123.72 5.56 2.063 7.245 1.435 1.802 3.645 2.726 6.57 2.747 2.642-.02 4.39-.654 5.84-2.122 1.654-1.675 1.626-3.731 1.097-4.974-.31-.733-.873-1.343-1.625-1.789-.193 1.366-.628 2.479-1.299 3.31-.895 1.111-2.16 1.715-3.755 1.794-1.207.061-2.367-.226-3.27-.81-1.07-.69-1.698-1.751-1.768-2.985-.143-2.535 1.873-4.36 5.147-4.658.872-.07 1.732-.083 2.572-.039.058-.349.075-.682.052-.987-.146-2.025-1.69-2.84-3.366-2.84-1.328 0-2.39.504-3.026 1.443l-1.67-1.222C9.165 6.42 10.79 5.69 12.66 5.69c2.69 0 5.21 1.477 5.522 4.59.166 1.66-.18 3.225-.998 4.526.55.346 1.024.792 1.405 1.331.64.91 1.005 2.027 1.005 3.144 0 1.87-.815 3.5-2.297 4.59C15.838 23.234 14.205 23.804 12.18 23.804z" },
                  ].map((icon) => (
                    <span key={icon.name} title={icon.name} className="opacity-70 hover:opacity-100 transition-opacity">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d={icon.path} /></svg>
                    </span>
                  ))}
                </div>
              </div>
              {/* Industry headlines ticker */}
              <p className="text-xs text-slate-300 mt-8 tracking-[0.1em] uppercase font-medium intelligence-badge mx-auto" data-testid="hero-micro-trust">
                <Zap size={10} className="text-blue-400" />
                Production &bull; AI Workforce &bull; Content Autopilot
              </p>
            </motion.div>
          </AnimatePresence>
          <div className="flex justify-center gap-2.5 mt-14" data-testid="banner-indicators">
            {BANNERS.map((_, i) => (
              <button key={i} onClick={() => goToBanner(i)} className={`h-[3px] rounded-full transition-all duration-500 ${i === activeBanner ? "w-12 bg-white shadow-[0_0_10px_rgba(255,255,255,0.4)]" : "w-4 bg-white/25 hover:bg-white/40"}`} aria-label={`Banner ${i + 1}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ──── MARQUEE ──── */}
      <section data-testid="marquee-section" className="bg-white py-5">
        <Marquee speed={30} gradient={false} pauseOnHover>
          {MARQUEE_ITEMS.map((item, i) => (
            <span key={i} className="flex items-center gap-3 text-slate-900 font-semibold text-sm tracking-wide uppercase px-6 whitespace-nowrap"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full" />{item}</span>
          ))}
        </Marquee>
      </section>

      {/* ──── OUR PROMISE (HeyGen company-vision style massive CTA) ──── */}
      <section data-testid="our-promise-section" className="relative bg-white py-24 md:py-32 overflow-hidden">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12">
          <div className="relative rounded-[28px] md:rounded-[36px] bg-gradient-to-br from-slate-50 via-white to-blue-50/60 border border-slate-200/70 overflow-hidden shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)]">
            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-0 items-center min-h-[460px] md:min-h-[560px]">

              {/* LEFT: copy block */}
              <motion.div {...fadeUp} className="px-8 sm:px-12 md:px-16 lg:px-20 py-14 lg:py-20 max-w-2xl">
                <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-blue-600 mb-6">
                  <span className="w-6 h-px bg-blue-600" /> Our Promise
                </span>
                <h2
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.2rem] font-black tracking-tight text-slate-900 leading-[1.02] mb-7"
                  style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  data-testid="our-promise-headline"
                >
                  Hand it over.<br />Properly.
                </h2>
                <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-10 max-w-md">
                  Stop juggling agencies, freelancers and AI tools. STRATIQ runs your website, your marketing, your ads, your content and your automation as one operation. One subscription. Real people behind it. Built for service businesses that want it done properly.
                </p>
                <button
                  onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }}
                  data-testid="our-promise-cta"
                  className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-600 text-white rounded-full px-7 sm:px-8 py-3.5 sm:py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(15,23,42,0.25)] cine-btn whitespace-nowrap"
                >
                  Talk to a STRATIQ Advisor <ArrowRight size={14} />
                </button>
              </motion.div>

              {/* RIGHT: ecosystem hero in a rounded floating frame */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative h-full w-full flex items-center justify-center px-6 md:px-10 lg:px-14 py-10 lg:py-12"
                data-testid="our-promise-visual"
              >
                <div
                  className="relative w-full max-w-[460px] aspect-square rounded-3xl md:rounded-[36px] bg-white border border-slate-200/60 overflow-hidden flex items-center justify-center p-6 md:p-8"
                  style={{ boxShadow: "0 30px 80px -25px rgba(15,23,42,0.18), 0 6px 18px -10px rgba(15,23,42,0.08)" }}
                >
                  <img
                    src="/promise/greenside_ecosystem.png"
                    alt="STRATIQ ecosystem: Smart Site, AI Front Desk, UGC content, Smart App dashboard"
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ──── INDUSTRY HEADLINES TICKER (Forbes / HBR / McKinsey) ──── */}
      <section data-testid="industry-headlines-ticker" className="bg-slate-950 border-y border-white/5 py-4">
        <Marquee speed={45} gradient={false} pauseOnHover>
          {AI_HEADLINES.map((line, i) => (
            <span key={i} className="flex items-center gap-3 px-10 text-xs sm:text-sm font-medium text-white/65 tracking-wide whitespace-nowrap">
              <span className="w-1 h-1 bg-blue-400 rounded-full" />{line}
            </span>
          ))}
        </Marquee>
      </section>

      {/* ──── FOUNDER VIDEO (Secondary Hero) ──── */}
      <section data-testid="founder-video-section" className="relative py-20 md:py-28 lg:py-32 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(59,130,246,0.1),transparent_60%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
            <motion.div {...fadeUp} className="flex-1 text-center lg:text-left max-w-2xl cine-reveal">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-blue-400 mb-4">Direct from the founder</p>
              <div className="space-y-3 mb-8">
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] ambient-heading">A few words from Aaron.</h2>
                <p className="text-lg sm:text-xl md:text-2xl font-medium text-white/85 tracking-tight leading-tight">No agency runaround. Just a real person, behind a real system.</p>
              </div>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">If you have ever felt let down by an agency, ghosted by a freelancer, or stuck with a website that does nothing for you, this is for you. Watch the message, then talk to one of our advisors when you are ready.</p>
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <button onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }} data-testid="founder-cta" className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-blue-600/25 hover:-translate-y-0.5 group cine-btn">Talk to a STRATIQ Advisor <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></button>
                <span className="text-sm text-slate-300 font-medium">10 advisors. Real time replies.</span>
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="flex-1 w-full max-w-xl">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-blue-900/40 border border-white/10 group">
                <video
                  src="/founder.mp4"
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full h-auto block bg-black"
                  data-testid="founder-video"
                />
                <div className="absolute top-4 left-4 bg-blue-600/90 backdrop-blur-sm text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">Aaron Deans &middot; Founder</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ──── INDUSTRY USE CASES (HeyGen 4-card grid pattern, STRATIQ blue palette) ──── */}
      <section data-testid="use-cases-section" className="relative py-24 md:py-36 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-14 md:mb-20 cine-reveal">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] max-w-4xl mx-auto ambient-heading-dark">
              Used by service businesses<br />
              who value <span className="text-blue-600">clarity, speed and flow.</span>
            </h2>
            <p className="text-lg text-slate-700 mt-6 max-w-2xl mx-auto leading-relaxed">
              See how operators across different niches scale enquiries, bookings and content with one subscription. One team behind it.
            </p>
          </motion.div>

          {/* 4 use case cards. Clean HeyGen style. White cards. Profile blob image. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7 cine-stagger" data-testid="use-case-grid">
            {[
              {
                title: "Trades",
                desc: "Plumbers, electricians and home service operators who need every enquiry captured the moment it lands. Calls, forms, WhatsApp, all routed and confirmed automatically.",
                photo: "/usecases/trades.png",
              },
              {
                title: "Dental",
                desc: "Dentists, orthodontists and implant clinics filling the chair. Online booking, deposits, automated reminders and quiet rebookings. Every patient confirmed.",
                photo: "/usecases/dental.png",
              },
              {
                title: "Fitness",
                desc: "PTs, gyms and studios running tight schedules. Class bookings, intake forms and confirmations handled in the background. You focus on the floor.",
                photo: "/usecases/fitness.png",
              },
              {
                title: "Beauty",
                desc: "Salons, clinics and therapists with a busy book. Online booking, deposits, reminders and rebookings handled quietly while you focus on the client in the chair.",
                photo: "/usecases/beauty.png",
              },
            ].map((card, i) => (
              <div
                key={i}
                data-testid={`use-case-card-${i}`}
                className="group relative rounded-3xl bg-white border border-slate-200/70 px-6 pt-8 pb-7 flex flex-col transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-100/60 hover:border-blue-200"
              >
                {/* Title row with arrow button */}
                <div className="flex items-start justify-between mb-3">
                  <h3
                    className="text-2xl md:text-[1.7rem] font-black tracking-[-0.02em] text-slate-950 leading-tight"
                    style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  >
                    {card.title}
                  </h3>
                  <button
                    onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }}
                    className="shrink-0 w-9 h-9 rounded-full bg-slate-100 group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all duration-300"
                    aria-label={`Open ${card.title} chat`}
                    data-testid={`use-case-cta-${i}`}
                  >
                    <ArrowRight size={15} />
                  </button>
                </div>

                {/* Profile blob image (glow + floating UI icon already baked into the asset) */}
                <div className="relative w-full aspect-square my-2">
                  <img
                    src={card.photo}
                    alt={`${card.title} operator`}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Description */}
                <p className="text-sm text-slate-600 leading-relaxed mt-3">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──── SMART SITE FRAMEWORK ──── */}
      <section
        data-testid="smartsite-explanation"
        className="relative py-24 md:py-32 bg-white overflow-hidden"
      >
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeUp}>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-4 block">The Smart Site Framework</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6 leading-tight ambient-heading-dark">Not just a website.<br />A working part of your<br />marketing department.</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-8">Every Smart Site is built to capture, organise and follow up every enquiry without you having to think about it. Stack A is fast and conversion ready. Stack B is bespoke, cinematic and high end. Both come fully wired into your tier.</p>
              <div className="grid grid-cols-2 gap-4">
                {[{ label: "Lead Capture", desc: "Every enquiry captured and routed" }, { label: "Booking Flows", desc: "Calendar synced, confirmations sent" }, { label: "Brand Layer", desc: "Premium, trustworthy, on brand" }, { label: "Always Updated", desc: "We keep it fresh, you focus on work" }].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm"><h4 className="text-sm font-bold text-slate-900 mb-1">{item.label}</h4><p className="text-xs text-slate-900">{item.desc}</p></div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full"
              data-testid="framework-carousel"
            >
              <FrameworkCarousel />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ──── AUTOMATION LAYER (HeyGen integrations panel + capability row) ──── */}
      <section data-testid="automation-section" className="py-24 md:py-32 bg-white overflow-hidden">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12">

          {/* Soft rounded panel with floating real integration logos */}
          <div className="relative rounded-[28px] md:rounded-[40px] overflow-hidden bg-gradient-to-br from-blue-50 via-sky-50/70 to-cyan-50/60 border border-blue-100/60" data-testid="automation-panel">
            {/* Subtle dotted texture */}
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #1e40af 1px, transparent 0)", backgroundSize: "26px 26px" }} />

            {/* Floating integration tiles. Real brand logos via simpleicons CDN. */}
            {[
              { name: "Stripe",          slug: "stripe",         color: "635BFF", top: "8%",   left: "4%" },
              { name: "HubSpot",         slug: "hubspot",        color: "FF7A59", top: "26%",  left: "11%" },
              { name: "Google Calendar", slug: "googlecalendar", color: "4285F4", top: "48%",  left: "3%" },
              { name: "Mailchimp",       slug: "mailchimp",      color: "FFE01B", top: "70%",  left: "11%" },
              { name: "Slack",           slug: "slack",          color: "4A154B", bottom: "6%", left: "4%" },

              { name: "WhatsApp",        slug: "whatsapp",       color: "25D366", top: "10%",  right: "4%" },
              { name: "Zapier",          slug: "zapier",         color: "FF4F00", top: "28%",  right: "12%" },
              { name: "OpenAI",          slug: "openai",         color: "0F172A", top: "50%",  right: "4%" },
              { name: "Twilio",          slug: "twilio",         color: "F22F46", top: "70%",  right: "12%" },
              { name: "Notion",          slug: "notion",         color: "0F172A", bottom: "6%", right: "4%" },
            ].map((icon, i) => (
              <div
                key={i}
                className="absolute hidden md:flex w-[58px] h-[58px] lg:w-[68px] lg:h-[68px] rounded-2xl bg-white shadow-[0_10px_30px_-10px_rgba(15,23,42,0.18)] items-center justify-center cine-float"
                style={{
                  top: icon.top,
                  bottom: icon.bottom,
                  left: icon.left,
                  right: icon.right,
                  animationDelay: `${(i % 5) * 0.4}s`,
                }}
                data-testid={`automation-integration-${icon.slug}`}
                aria-hidden="true"
              >
                <img
                  src={`https://api.iconify.design/simple-icons:${icon.slug}.svg?color=%23${icon.color}`}
                  alt={icon.name}
                  className="w-7 h-7 lg:w-8 lg:h-8 object-contain"
                  loading="lazy"
                />
              </div>
            ))}

            {/* Center column */}
            <div className="relative z-10 max-w-2xl mx-auto text-center px-6 md:px-12 py-20 md:py-32 lg:py-40">
              <motion.div {...fadeUp} className="cine-reveal">
                <span className="inline-block text-[11px] font-bold tracking-[0.3em] uppercase text-blue-600 mb-6">
                  Automation Layer&#8482;
                </span>
                <h2
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.6rem] font-black tracking-[-0.025em] text-slate-950 leading-[1.0] mb-7"
                  style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  data-testid="automation-headline"
                >
                  Zero missed enquiries.<br />
                  Zero manual work.
                </h2>
                <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-md mx-auto mb-10">
                  Your website captures, organises and follows up. Automatically. Connected to the tools your business already runs on.
                </p>
                <button
                  onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }}
                  data-testid="automation-cta"
                  className="inline-flex items-center gap-2 bg-slate-950 hover:bg-blue-600 text-white rounded-full px-8 py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.45)] cine-btn"
                >
                  Explore the integrations <ArrowRight size={14} />
                </button>
              </motion.div>
            </div>
          </div>

          {/* Capability row (HeyGen "superpower" pattern: big bold headline + 5 icon cards) */}
          <div className="mt-20 md:mt-28" data-testid="automation-capabilities">
            <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-12 md:mb-16 cine-reveal">
              <h3
                className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.4rem] font-black tracking-[-0.025em] leading-[1.0] text-slate-950"
                style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                data-testid="superpower-headline"
              >
                Running your operation<br />
                has just become your <span className="text-slate-300">superpower</span>.
              </h3>
              <p className="text-base md:text-lg text-slate-600 mt-7 max-w-xl mx-auto leading-relaxed">
                Built into every STRATIQ subscription. Wired into the tools you already run. Live from day one. No extra cost, no setup fees.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {[
                { icon: Inbox,        label: "Lead Capture" },
                { icon: CalendarIcon, label: "Smart Booking" },
                { icon: BellRing,     label: "Auto Confirmations" },
                { icon: Database,     label: "CRM Sync" },
                { icon: BarChart3,    label: "Performance Reports" },
              ].map((cap, i) => (
                <div
                  key={i}
                  data-testid={`capability-card-${i}`}
                  className="group relative rounded-3xl bg-slate-100 hover:bg-white border border-transparent hover:border-blue-200 px-4 py-10 md:py-14 flex flex-col items-center text-center transition-all duration-300 hover:shadow-lg hover:shadow-blue-100/50 hover:-translate-y-0.5"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white border border-slate-200/80 flex items-center justify-center mb-5 md:mb-6 shadow-sm group-hover:scale-105 transition-transform">
                    <cap.icon size={24} className="text-slate-900" strokeWidth={2.2} />
                  </div>
                  <p
                    className="text-base md:text-lg lg:text-xl font-black text-slate-950 tracking-tight leading-tight"
                    style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  >
                    {cap.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ──── DIGITAL ASSISTANT ──── */}
      <section data-testid="assistant-section" className="relative bg-white overflow-hidden py-20 md:py-28">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-16 items-center">
            <motion.div {...fadeUp} className="max-w-xl mx-auto lg:mx-0 text-center lg:text-left order-1 lg:order-1">
              <span className="text-sm font-semibold tracking-[0.2em] uppercase text-blue-600 mb-5 block">AI Front Desk</span>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.0] mb-8 ambient-heading-dark">Always<br />available.</h2>
              <p className="text-blue-600 font-semibold text-lg md:text-xl mb-5">From first message to confirmed booking</p>
              <p className="text-base md:text-lg text-slate-900 leading-relaxed mb-10 max-w-lg mx-auto lg:mx-0">Your AI Front Desk replies in seconds, qualifies the lead, checks live availability, books the appointment and sends every confirmation. Day or night. Channel agnostic. Quietly handled.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/smartmatch" data-testid="assistant-cta-primary" className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white hover:bg-blue-600 rounded-full px-10 py-4 text-base font-semibold transition-all duration-300 hover:-translate-y-0.5">See it in action <ArrowRight size={16} /></Link>
                <Link to="/book" data-testid="assistant-cta-secondary" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 rounded-full px-10 py-4 text-base font-semibold transition-all duration-300 hover:-translate-y-0.5">Book a call</Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full order-2 lg:order-2"
            >
              <img
                src="/ai_front_desk_flow.png"
                alt="AI Front Desk Intake Flow: from first message to confirmed booking"
                className="w-full h-auto object-contain"
                loading="lazy"
                data-testid="assistant-device-image"
                style={{ filter: "drop-shadow(0 30px 60px rgba(15,23,42,0.10))" }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ──── CONTENT AUTOPILOT (HeyGen UGC adverts pattern) ──── */}
      <section data-testid="perception-section" className="relative py-20 md:py-32 bg-white overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 xl:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-14 lg:gap-10 items-center">

            {/* LEFT: copy block */}
            <motion.div {...fadeUp}>
              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-blue-600 mb-6">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-blue-600">
                    <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />
                  </svg>
                </span>
                UGC Content Autopilot
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.04] mb-7">
                We get your brand.<br />
                <span className="text-blue-600">We make the content.</span><br />
                We post it for you.
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-9 max-w-lg">
                Stop wrestling with TikTok, Instagram and YouTube Shorts on your own. We learn your brand voice, build a posting schedule that actually fits your business, then produce real UGC style product reviews, app walkthroughs, website demos and client stories. Every week. Every channel. Quietly handled.
              </p>

              <div className="space-y-3.5 mb-10">
                {[
                  "We onboard your brand. Tone, voice, audience, offers, the lot.",
                  "We build your posting schedule across TikTok, Reels and Shorts.",
                  "We script and produce UGC reviews, demos and client stories.",
                  "We post, caption, hashtag and report on what is moving the needle.",
                ].map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={13} className="text-blue-600" strokeWidth={3} />
                    </div>
                    <p className="text-base text-slate-800 leading-relaxed">{line}</p>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <button
                  onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }}
                  data-testid="perception-cta-primary"
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(37,99,235,0.35)] whitespace-nowrap w-full sm:w-auto cine-btn"
                >
                  Talk to a STRATIQ Advisor <ArrowRight size={14} />
                </button>
                <a
                  href="https://wa.me/447368945543"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="perception-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#1fb855] rounded-full px-8 py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap w-full sm:w-auto"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp Us
                </a>
              </div>
            </motion.div>

            {/* RIGHT: UGC video stack with platform icons + blue halo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="perception-mockup"
              className="relative w-full aspect-[5/4] flex items-center justify-center"
            >
              {/* Subtle blue static gradient halo */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[78%] h-[80%] rounded-full bg-gradient-to-br from-blue-200 via-blue-100 to-cyan-100 opacity-80 blur-3xl" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[55%] h-[55%] rounded-full bg-gradient-to-br from-blue-300/60 to-cyan-200/60 opacity-70 blur-2xl" />
              </div>

              {/* 3 UGC phone mockups in fan layout */}
              <div className="absolute inset-0 z-10 flex items-center justify-center gap-3 md:gap-5 px-6">
                {[
                  {
                    img: "/ugc/ugc_trades.png",
                    alt: "@apexplumbing.uk Reel review",
                    rotate: "-rotate-6 -translate-y-2",
                    z: "z-10",
                  },
                  {
                    img: "/ugc/ugc_beauty.png",
                    alt: "@luxe.beauty.studio Reel review",
                    rotate: "rotate-0 -translate-y-6",
                    z: "z-20",
                  },
                  {
                    img: "/ugc/ugc_fitness.png",
                    alt: "@forge.training Reel review",
                    rotate: "rotate-6 -translate-y-2",
                    z: "z-10",
                  },
                ].map((card, i) => (
                  <div
                    key={i}
                    className={`relative ${card.rotate} ${card.z} w-[150px] sm:w-[170px] md:w-[180px] lg:w-[200px] aspect-[9/16] rounded-[22px] overflow-hidden bg-slate-900 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.45)] ring-1 ring-white/40 flex-shrink-0`}
                    data-testid={`ugc-card-${i}`}
                  >
                    <img src={card.img} alt={card.alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>

              {/* Floating platform icons orbiting the stack */}
              {[
                { name: "TikTok", top: "8%", left: "10%", delay: 0, svg: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-slate-900"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/></svg> },
                { name: "Instagram", top: "12%", right: "8%", delay: 0.4, svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-pink-600"><rect x="3" y="3" width="18" height="18" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.5" y2="6.5"/></svg> },
                { name: "YouTube", bottom: "18%", left: "5%", delay: 0.8, svg: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-red-600"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6z"/></svg> },
                { name: "Threads", bottom: "10%", right: "10%", delay: 1.2, svg: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-slate-900"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.85 1.205 8.605.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.781 3.631 2.695 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.74-1.757-.502-.584-1.278-.88-2.309-.88h-.028c-.829.013-2.245.273-2.99 1.5l-1.69-1.155c.969-1.49 2.547-2.358 4.642-2.397h.045c3.498 0 5.587 2.146 5.797 5.913.12.05.237.105.354.16 1.6.752 2.778 1.892 3.408 3.301.901 2.014.984 5.337-1.83 8.087-1.86 1.815-4.087 2.665-7.157 2.685z"/></svg> },
              ].map((icon, i) => (
                <div
                  key={i}
                  className="absolute z-30 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-white shadow-[0_8px_24px_rgba(15,23,42,0.18)] flex items-center justify-center cine-float"
                  style={{
                    top: icon.top,
                    left: icon.left,
                    right: icon.right,
                    bottom: icon.bottom,
                    animationDelay: `${icon.delay}s`,
                  }}
                  data-testid={`ugc-platform-icon-${i}`}
                >
                  {icon.svg}
                </div>
              ))}
            </motion.div>

          </div>
        </div>
      </section>

      {/* ──── DISCOVER SMART SITES (centered title, single-line auto-marquee carousel) ──── */}
      <section data-testid="what-we-do-section" className="relative bg-slate-950 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-14 md:pb-16">
          <motion.div {...fadeUp} className="cine-reveal text-center max-w-3xl mx-auto">
            <h2
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] text-white leading-[1.02] ambient-heading"
              style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
              data-testid="discover-headline"
            >
              Discover Smart Sites
            </h2>
            <p className="text-base md:text-lg text-slate-400 mt-6 leading-relaxed mx-auto max-w-2xl">
              Real builds for real service businesses. Picked from across our portfolio.
            </p>
            <div className="mt-8 flex justify-center">
              <Link
                to="/products"
                data-testid="discover-view-all"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded-full px-6 py-3 transition-all"
              >
                View all <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* One-line auto-rotating marquee carousel */}
        <div className="pb-24 md:pb-36">
          <div className="relative" data-testid="portfolio-grid">
            {/* Edge fade masks */}
            <div className="absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none" />
            <Marquee speed={45} gradient={false} pauseOnHover={true}>
              {[
                { title: "Bowery Barbers", category: "Grooming", description: "Premium men's grooming brand with chair booking flow.", img: "/portfolio/bowery_barbers.png" },
                { title: "Solace Yoga Studio", category: "Wellness", description: "Calm, considered studio site with class scheduling.", img: "/portfolio/solace_yoga.png" },
                { title: "Aurora Wedding Co.", category: "Photography", description: "Cinematic wedding photographer with package showcase.", img: "/portfolio/aurora_weddings.png" },
                { title: "Crest Auto Detailing", category: "Auto", description: "Editorial detailer brand with appointment booking.", img: "/portfolio/crest_auto.png" },
                { title: "Atrium Architects", category: "Architecture", description: "Studio site engineered for serious project enquiries.", img: "/portfolio/atrium_architects.png" },
                { title: "Verde Garden Co.", category: "Landscaping", description: "Premium landscape brand with portfolio and quotes.", img: "/portfolio/verde_gardens.png" },
                { title: "Forge Personal Training", category: "Fitness", description: "Operator focused PT brand with intake and booking.", img: "/portfolio/forge_pt.png" },
                { title: "Saffron Catering", category: "Catering", description: "Refined catering brand with event enquiry intake.", img: "/portfolio/saffron_catering.png" },
              ].map((site, idx) => (
                <Tilt key={idx} intensity={8} scale={1.03} className="mx-3 md:mx-4 w-[260px] sm:w-[320px] md:w-[380px] lg:w-[420px]" testId={`portfolio-card-${idx}`}>
                  <Link
                    to="/products"
                    className="group block focus:outline-none"
                  >
                    <div className="relative">
                      {/* Subtle blue glow underneath */}
                      <div className="absolute -inset-x-3 -bottom-4 h-12 bg-blue-500/30 blur-2xl rounded-full opacity-70 group-hover:opacity-100 group-hover:bg-blue-400/45 transition-all duration-500 pointer-events-none" />
                      <div className="relative rounded-2xl overflow-hidden bg-slate-900 border border-white/5 group-hover:border-blue-400/40 transition-colors duration-500 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
                        <div className="aspect-[16/10] overflow-hidden bg-slate-900">
                          <img
                            src={site.img}
                            alt={site.title}
                            className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 px-1 relative z-10">
                      <h3 className="text-base md:text-lg font-bold text-white tracking-tight leading-snug">{site.title}</h3>
                      <p className="text-sm text-slate-400 mt-1 leading-snug line-clamp-1">{site.description}</p>
                    </div>
                  </Link>
                </Tilt>
              ))}
            </Marquee>
          </div>

          {/* Three Arms strip (HeyGen black tile aesthetic) */}
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-testid="three-arms-strip">
            {[
              { arm: "01", title: "Production", desc: "Smart Sites. Hosting. Maintenance." },
              { arm: "02", title: "AI Workforce", desc: "Voice AI. Conversation AI. Automation." },
              { arm: "03", title: "Content Autopilot", desc: "SEO engine. Social. Daily cadence." },
            ].map((arm, i) => (
              <div
                key={i}
                data-testid={`arm-tile-${i}`}
                className="relative rounded-3xl bg-black px-8 py-12 md:py-14 flex flex-col items-center text-center transition-transform duration-500 hover:-translate-y-1"
              >
                <span className="text-[11px] font-bold tracking-[0.35em] uppercase text-blue-500 mb-4">Arm {arm.arm}</span>
                <h4 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-3 leading-[1]">{arm.title}</h4>
                <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-[260px]">{arm.desc}</p>
              </div>
            ))}
          </div>

          {/* AI industry leaders marquee (homage row) */}
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-12 md:mt-16" data-testid="ai-leaders-marquee">
            <p className="text-center text-[10px] font-bold tracking-[0.35em] uppercase text-white/40 mb-6">Powered by the Leaders in AI</p>
            <div className="relative overflow-hidden border-y border-white/[0.06] py-7">
              <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none" />
              <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none" />
              <Marquee speed={45} gradient={false} pauseOnHover={false}>
                {[
                  "OpenAI", "Anthropic", "Google Gemini", "Meta AI", "Microsoft AI",
                  "NVIDIA", "ElevenLabs", "Hugging Face", "Mistral", "Stability AI",
                  "Perplexity", "Cohere",
                ].map((name, i) => (
                  <span
                    key={i}
                    className="mx-10 md:mx-14 text-2xl md:text-3xl font-bold tracking-tight text-white/55 hover:text-white transition-colors duration-300 whitespace-nowrap select-none"
                    style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  >
                    {name}
                  </span>
                ))}
              </Marquee>
            </div>
          </div>
        </div>
      </section>

      {/* ──── 90 DAY JOURNEY ──── */}
      <section data-testid="ninety-day-journey" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start lg:items-center">
            <motion.div {...fadeUp} className="cine-reveal">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-4 block">Your 90 Day Journey</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6 ambient-heading-dark">From signup to compounding<br />in three months.</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-10">No agency runaround. We onboard, build, optimise, and compound. You see real movement every fortnight.</p>
              <div>
                <ProcessStep number={1} title="Onboarding (Days 1 to 7)" desc="Kickoff call, brand voice capture, asset gathering, calendar and CRM connections. We learn your business inside out." icon={Globe} />
                <ProcessStep number={2} title="Build (Days 8 to 30)" desc="Smart Site goes live. AI Workforce wired up. Content engine seeded. Lead capture tested end to end." icon={Eye} />
                <ProcessStep number={3} title="Optimisation (Days 31 to 60)" desc="We tune ads, refine the AI agents, expand content cadence, and start moving leads into customers." icon={Zap} />
                <ProcessStep number={4} title="Compounding (Days 61 to 90)" desc="The system starts running on its own. SEO compounds. Conversions improve. You focus on delivering work." icon={TrendingUp} isLast />
              </div>
              <button onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }} data-testid="ninety-day-cta" className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-3.5 text-sm font-medium transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 mt-6 cine-btn">Talk to a STRATIQ Advisor <ArrowRight size={14} /></button>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.7 }}>
              <div className="texture-card-elevated p-0 overflow-hidden">
                <div className="bg-slate-950 text-white px-8 py-6">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-blue-400 font-semibold mb-2">Most Popular Tier</p>
                  <h3 className="text-xl font-bold">GROWTH</h3>
                  <span className="text-blue-400 text-lg font-bold">&pound;997 / month</span>
                </div>
                <div className="p-8 space-y-5">
                  {[
                    { label: "What You Get", text: "Smart Site, paid ads management, Conversation AI, weekly content publishing and basic AI Front Desk." },
                    { label: "Optional Add On", text: "Content Autopilot Starter at \u00a3497 a month for weekly SEO content and monthly social repurposing." },
                    { label: "When To Upgrade", text: "Once your inbound is steady, move to PRO for Voice AI, Automation Hub and daily content." },
                  ].map((s, i) => (
                    <div key={i}><p className="text-[10px] font-bold tracking-[0.15em] uppercase text-blue-600 mb-1">{s.label}</p><p className="text-sm text-slate-900 leading-relaxed">{s.text}</p></div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ──── SUBSCRIPTION TIERS ──── */}
      <section data-testid="tiers-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16 cine-reveal">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-4 block">One Subscription. Real People Behind It.</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4 ambient-heading-dark">Pick the tier that fits, scale up when you are ready.</h2>
            <p className="text-lg text-slate-900 max-w-xl mx-auto">No setup minefield. No retainer wars. Pause or cancel any time after your first 30 days.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 cine-stagger">
            {TIERS.map((tier, i) => {
              const gradients = [
                "bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900",
                "bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700",
                "bg-gradient-to-br from-violet-500 via-purple-600 to-violet-700",
                "bg-gradient-to-br from-amber-500 via-orange-600 to-rose-700",
              ];
              return (
                <Tilt key={i} intensity={12} scale={1.04} testId={`tier-card-${i}`} className="h-full">
                  <div className="relative flex flex-col h-full bg-white rounded-2xl shadow-lg shadow-slate-200/50 overflow-hidden cursor-pointer group transition-shadow duration-500 hover:shadow-2xl hover:shadow-blue-200/40">
                    {tier.popular && <span className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1 rounded-full z-10">Most Popular</span>}
                    <div className={`${gradients[i]} px-6 py-8 text-white`}>
                      <p className="text-xs font-semibold tracking-wider uppercase text-white/70 mb-2">Subscription Tier</p>
                      <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-extrabold">{tier.price}</span>
                        <span className="text-sm text-white/75">{tier.period}</span>
                      </div>
                    </div>
                    <div className="px-6 py-6 flex flex-col flex-1">
                      <p className="text-sm text-slate-900 mb-5 leading-relaxed">{tier.desc}</p>
                      <ul className="space-y-2 mb-6">
                        {tier.features.slice(0, 4).map((f, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-slate-700"><Check size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>
                        ))}
                      </ul>
                      <Link to="/pricing" data-testid={`tier-cta-${i}`} className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 py-3 text-sm font-medium transition-all w-full group-hover:bg-blue-600 mt-auto cine-btn">View {tier.name} <ChevronRight size={14} /></Link>
                    </div>
                  </div>
                </Tilt>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──── FEATURED CUSTOMER STORY (HeyGen pattern) ──── */}
      <section data-testid="featured-story-section" className="relative py-24 md:py-36 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-20 items-center">

            {/* LEFT: pill + quote + name + 2 stat cards */}
            <motion.div {...fadeUp} className="cine-reveal">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-slate-300 text-[11px] font-semibold tracking-wide text-slate-700 bg-white shadow-sm">
                Featured customer story
              </span>

              <h2 className="mt-8 text-2xl md:text-3xl lg:text-[2.4rem] xl:text-[2.6rem] font-bold tracking-tight text-slate-900 leading-[1.18] max-w-xl">
                "Honestly, the STRATIQ team made the whole thing feel easy. Our advisor walked me through every step and got us booking jobs faster than I expected. It is the first time I have actually enjoyed working with a marketing team."
              </h2>

              <div className="mt-8">
                <p className="text-base font-bold text-slate-900">James Carter</p>
                <p className="text-sm text-slate-600 mt-0.5">Founder, Carter Plumbing and Heating</p>
              </div>

              {/* Stat cards. STRATIQ blue gradient borders */}
              <div className="mt-10 grid grid-cols-2 gap-4 max-w-md" data-testid="featured-story-stats">
                {[
                  { value: "3x", label: "increase in booked jobs per week" },
                  { value: "0", label: "missed enquiries since launch" },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="relative rounded-2xl p-[1.5px] bg-gradient-to-br from-blue-400 via-blue-500 to-cyan-400"
                  >
                    <div className="rounded-2xl bg-white px-6 py-7 h-full">
                      <p className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-none mb-3" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>{stat.value}</p>
                      <p className="text-xs md:text-sm text-slate-600 leading-snug">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* RIGHT: founder photo with blue gradient blobs */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full aspect-[4/5] max-w-[520px] mx-auto"
              data-testid="featured-story-portrait"
            >
              {/* Big blue blob behind */}
              <div
                className="absolute top-[8%] left-[10%] right-[8%] bottom-[12%] rounded-[42%_58%_52%_48%/55%_45%_55%_45%] bg-gradient-to-br from-blue-400 via-blue-500 to-cyan-400 shadow-[0_30px_80px_-20px_rgba(37,99,235,0.45)]"
              />
              {/* Light cyan accent blob */}
              <div
                className="absolute top-[55%] left-[-4%] w-[34%] aspect-square rounded-[58%_42%_45%_55%/48%_52%_48%_52%] bg-gradient-to-br from-cyan-200 to-blue-300 opacity-90"
              />
              {/* Deep blue accent blob bottom-right */}
              <div
                className="absolute bottom-[2%] right-[-2%] w-[38%] aspect-[5/4] rounded-[55%_45%_50%_50%/45%_55%_45%_55%] bg-gradient-to-br from-blue-600 to-indigo-700 opacity-95 flex items-center justify-center"
              >
                <span className="text-white font-black text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>Carter</span>
              </div>

              {/* Founder photo. Cropped to a soft rounded shape sitting in front of the blue blob */}
              <div className="absolute top-[5%] left-[14%] right-[10%] bottom-[8%] rounded-[42%_58%_52%_48%/55%_45%_55%_45%] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1764173039051-987d1639b9d5?w=900&h=1100&fit=crop&crop=faces&q=85"
                  alt="James Carter, Founder of Carter Plumbing and Heating"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Soft outer glow */}
              <div className="absolute inset-0 rounded-[42%_58%_52%_48%/55%_45%_55%_45%] pointer-events-none" style={{ boxShadow: "0 60px 120px -40px rgba(37,99,235,0.35)" }} />
            </motion.div>

          </div>
        </div>
      </section>

      {/* ──── VALUE BANNER ──── */}
      <section data-testid="value-banner" className="relative py-6 bg-slate-950 overflow-hidden">
        <Marquee speed={25} gradient={false} direction="right">
          {["Production", "AI Workforce", "Content Autopilot", "Voice AI", "Conversation AI", "Automation Hub", "Stack A", "Stack B", "Smart Site", "Founder Direct"].map((item, i) => (
            <span key={i} className="flex items-center gap-4 px-6 text-sm font-medium text-white/80 tracking-wide uppercase whitespace-nowrap"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />{item}</span>
          ))}
        </Marquee>
      </section>

      {/* ──── FINAL CTA (HeyGen style: gradient ribbons + bold black headline + pill CTA) ──── */}
      <section
        data-testid="final-cta-section"
        className="relative w-full min-h-[70vh] md:min-h-[88vh] overflow-hidden flex items-center bg-white"
      >
        {/* Gradient ribbons background */}
        <img
          src="/promise/final_cta_ribbons.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          loading="lazy"
        />
        {/* Soft white center wash so text always reads cleanly */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.78),rgba(255,255,255,0.35)_55%,transparent_80%)] pointer-events-none" />

        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10 w-full py-24 md:py-36">
          <motion.div {...fadeUp}>
            <h2
              className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.6rem] font-black tracking-[-0.03em] text-slate-950 leading-[1.0] mb-8"
              style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
              data-testid="final-cta-headline"
            >
              Ready to hand it over?
            </h2>
            <p className="text-base md:text-lg text-slate-700 max-w-xl mx-auto mb-12 leading-relaxed">
              One subscription. Real people behind it. We run your marketing department.<br className="hidden md:block" />
              You run your business.
            </p>
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent('open-assistant')); } catch (e) {} }}
              data-testid="final-cta-button"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full px-10 py-5 text-base md:text-lg font-bold tracking-tight transition-all duration-300 hover:-translate-y-0.5 shadow-[0_20px_60px_-15px_rgba(37,99,235,0.55)] hover:shadow-[0_25px_70px_-15px_rgba(37,99,235,0.7)] cine-btn"
            >
              Get started from £497 <ArrowRight size={18} />
            </button>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mt-7" data-testid="cta-free-report">
              Production &middot; AI Workforce &middot; Content Autopilot
            </p>
          </motion.div>
        </div>
      </section>

      {/* ──── TRUST MARQUEE ──── */}
      <section data-testid="trust-marquee-banner" className="bg-white py-5 border-b border-slate-100">
        <Marquee speed={35} gradient={true} gradientColor="white" gradientWidth={80} pauseOnHover>
          {["Marketing", "Ads", "Website", "Automation", "Voice AI", "Conversation AI", "Content Autopilot", "All Handled", "All In One Place"].map((item, i) => (
            <span key={i} className="flex items-center gap-4 px-8 text-sm font-bold text-slate-900 tracking-wide uppercase whitespace-nowrap hover:text-blue-600 transition-colors cursor-default"><span className="w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0" />{item}</span>
          ))}
        </Marquee>
      </section>
    </main>
  );
}
